import React from 'react';

/**
 * Vibrant animated Signal Orb — a radiant multi-color visual with:
 * - Rotating conic-gradient aurora backdrop
 * - Morphing gradient core sphere
 * - Multi-orbit ring paths with glowing particles
 * - Radial light rays + soft glass panel
 */
export default function SignalOrb({ className = '' }) {
  const size = 320;
  const center = size / 2;

  // Multiple orbit radii + particle configs
  const orbits = [
    { r: 62, count: 3, speed: 8, dir: 1, colors: ['#FF3B7F', '#00E5FF', '#FBBC05'] },
    { r: 96, count: 4, speed: 14, dir: -1, colors: ['#9333EA', '#10B981', '#FF6B35', '#00E5FF'] },
    { r: 128, count: 6, speed: 22, dir: 1, colors: ['#FF3B7F', '#4ECDC4', '#FBBC05', '#9333EA', '#10B981', '#FF6B35'] },
  ];

  return (
    <div className={`relative w-full h-full overflow-hidden rounded-xl ${className}`}>
      {/* Animated aurora backdrop */}
      <div className="absolute inset-0 opacity-70 signal-aurora" />

      {/* Soft dark base gradient for legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, rgba(15,28,64,0.15) 0%, rgba(15,28,64,0.85) 90%)',
        }}
      />

      {/* Grid texture overlay */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Glow gradients */}
          <radialGradient id="signal-core">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
            <stop offset="30%" stopColor="#00E5FF" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#9333EA" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#FF3B7F" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="signal-halo">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0" />
            <stop offset="70%" stopColor="#00E5FF" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Radiating light rays */}
        <g style={{ transformOrigin: `${center}px ${center}px` }} className="signal-rays">
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i / 12) * Math.PI * 2;
            const x1 = center + Math.cos(angle) * 40;
            const y1 = center + Math.sin(angle) * 40;
            const x2 = center + Math.cos(angle) * 150;
            const y2 = center + Math.sin(angle) * 150;
            const color = ['#FF3B7F', '#00E5FF', '#FBBC05', '#9333EA', '#10B981', '#FF6B35'][i % 6];
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={color}
                strokeOpacity="0.25"
                strokeWidth="1"
                strokeLinecap="round"
              />
            );
          })}
        </g>

        {/* Outer halo pulses */}
        {[0, 1, 2].map((i) => (
          <circle
            key={`halo-${i}`}
            cx={center}
            cy={center}
            r="60"
            fill="none"
            stroke="#00E5FF"
            strokeOpacity="0.6"
            strokeWidth="1.5"
            className="signal-ring"
            style={{ animationDelay: `${i * 1.1}s`, transformOrigin: `${center}px ${center}px` }}
          />
        ))}

        {/* Big soft halo */}
        <circle cx={center} cy={center} r="140" fill="url(#signal-halo)" />

        {/* Orbits + particles */}
        {orbits.map((orbit, oi) => (
          <g key={`orbit-${oi}`}>
            {/* orbit path */}
            <circle
              cx={center}
              cy={center}
              r={orbit.r}
              fill="none"
              stroke="rgba(255,255,255,0.12)"
              strokeDasharray="2 4"
              strokeWidth="0.8"
            />
            {/* rotating particle group */}
            <g style={{ transformOrigin: `${center}px ${center}px` }}>
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`0 ${center} ${center}`}
                to={`${orbit.dir * 360} ${center} ${center}`}
                dur={`${orbit.speed}s`}
                repeatCount="indefinite"
              />
              {Array.from({ length: orbit.count }).map((_, i) => {
                const angle = (i / orbit.count) * Math.PI * 2;
                const x = center + Math.cos(angle) * orbit.r;
                const y = center + Math.sin(angle) * orbit.r;
                const color = orbit.colors[i % orbit.colors.length];
                return (
                  <g key={i}>
                    {/* soft glow */}
                    <circle cx={x} cy={y} r="14" fill={color} opacity="0.25" />
                    {/* particle core */}
                    <circle cx={x} cy={y} r="5" fill={color} style={{ filter: `drop-shadow(0 0 8px ${color})` }}>
                      <animate attributeName="r" values="4;6.5;4" dur="1.8s" repeatCount="indefinite" begin={`${i * 0.2}s`} />
                    </circle>
                    <circle cx={x} cy={y} r="3" fill="white" opacity="0.9" />
                  </g>
                );
              })}
            </g>
          </g>
        ))}

        {/* Central Core */}
        <circle cx={center} cy={center} r="55" fill="url(#signal-core)" className="signal-core-pulse" style={{ transformOrigin: `${center}px ${center}px` }} />
        <circle cx={center} cy={center} r="14" fill="white" opacity="0.95">
          <animate attributeName="r" values="12;16;12" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx={center} cy={center} r="8" fill="#00E5FF" />
      </svg>

      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 50% 50%, transparent 40%, rgba(15,28,64,0.55) 100%)' }}
      />
    </div>
  );
}
